/**
 * Which Gemini model does which job (1.27, Mike 26.10.: "die richtigen Modelle für die richtigen
 * Aufgaben einsetzen"). Two attempts at most – the second only when the first failed, and then
 * on the other model: Google's capacity is shared per model, a busy one says nothing about the
 * other.
 *
 * Measured, not guessed (26.10., functions/test/ai.live.test.ts, docs/ki-messung.md): every model
 * in every location with the real test set and Mike's photos. Both models run on Vertex AI in the
 * EU multi-region `eu` (processed only in EU member states). Frankfurt alone offers only
 * gemini-3.5-flash and answered "busy" (429) for half the photos; gemini-3.8-flash exists only
 * globally (no EU promise). Any change here: measure first (.claude/rules/ki.md).
 */
export type AiTask =
  /** "Einfach sagen" with a sentence (typed or spoken) */
  | 'sentence'
  /** "Einfach sagen" with a photo: paper calendar, timetable, school letter */
  | 'photo'
  /** a voucher photo (Freizeit) */
  | 'voucher'
  /** a recipe from a photo or screenshot */
  | 'recipePhoto'
  /** a recipe from a page's text ("Mit KI aufräumen") */
  | 'recipeText'
  /** three new meal ideas */
  | 'ideas';

export const AI_TASKS: readonly AiTask[] = [
  'sentence',
  'photo',
  'voucher',
  'recipePhoto',
  'recipeText',
  'ideas',
];

export const LITE = 'gemini-3.1-flash-lite';
export const FLASH = 'gemini-3.5-flash';

/**
 * First and second attempt per job. Lite: the test set 14/15 in under 1 s, the timetable photo
 * complete in 2 s. Flash reads recipe photos more thoroughly (each filling ingredient on its own)
 * and is the more inventive cook.
 */
export const TASK_MODELS: Record<AiTask, readonly [string, string]> = {
  sentence: [LITE, FLASH],
  photo: [LITE, FLASH],
  voucher: [LITE, FLASH],
  recipePhoto: [FLASH, LITE],
  recipeText: [LITE, FLASH],
  ideas: [FLASH, LITE],
};

/**
 * US$ per million tokens (input, output) on Vertex AI – research 26.10.; check Google's price
 * page before relying on it. Thinking tokens are billed as output.
 * Only for the admin's estimate of what the AI costs – nothing is billed from this.
 */
export const MODEL_PRICES: Record<string, { input: number; output: number }> = {
  [LITE]: { input: 0.25, output: 1.5 },
  [FLASH]: { input: 1.5, output: 9 },
};

/** Estimated cost in US cents; unknown models count as Flash (the dearer guess). */
export function costCents(model: string, tokensIn: number, tokensOut: number): number {
  const p = MODEL_PRICES[model] ?? MODEL_PRICES[FLASH]!;
  return ((tokensIn * p.input + tokensOut * p.output) / 1_000_000) * 100;
}
